import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import {
  Compass,
  Layers,
  Mountain,
  Trees,
  ArrowRight,
  Sparkles,
  Check,
  Star,
  Ear,
  FolderSearch,
  MapPin,
  CheckCircle,
  Package,
} from "lucide-react";
import Nav from "@/components/Nav";
import Footer from "@/components/Footer";
import DownloadPromo from "@/components/DownloadPromo";

const HERO_BG =
  "https://images.unsplash.com/photo-1618661733134-6a750e4aa08a?crop=entropy&cs=srgb&fm=jpg&q=85&w=2400";

const DISCIPLINES = [
  "Healthy Country Planning",
  "Cultural mapping",
  "Ecological assessment",
  "Restoration planning",
  "Ranger knowledge transfer",
  "Funding and grant support",
  "Community engagement",
  "Visual storytelling",
];

const TIERS = [
  {
    icon: Compass,
    n: "Tier 01",
    name: "Desktop Bush Map",
    price: "$2,500 – $8,500",
    tagline: "Introductory · desk-based",
    suitable: [
      "Small ranger groups",
      "Schools",
      "Introductory projects",
      "Funding applications",
    ],
    includes: [
      "Existing ecological data review",
      "Species lists",
      "Landscape layers",
      "One illustrated Bush Map",
      "Short explanatory report",
    ],
    note: "No major on-Country engagement.",
  },
  {
    icon: Layers,
    n: "Tier 02",
    name: "Community Bush Map",
    price: "$10,000 – $25,000",
    tagline: "Perfect for grant-funding and community engagement",
    featured: true,
    suitable: [
      "Ranger groups",
      "Community organisations",
      "NRM groups",
      "Funding-ready projects",
    ],
    includes: [
      "Desktop research",
      "Existing reports review",
      "Ranger workshops",
      "Elder interviews",
      "Species and cultural values mapping",
      "Landscape interpretation",
      "Illustrated Bush Map",
      "Funding-ready report",
      "Presentation to community",
    ],
    note: "This becomes a useful planning tool.",
  },
  {
    icon: Mountain,
    n: "Tier 03",
    name: "Bush Mapping for IPA, Carbon or Restoration Planning",
    price: "$25,000 – $50,000",
    tagline: "Strategic planning infrastructure",
    suitable: [
      "Indigenous Protected Areas",
      "Restoration programs",
      "Carbon project proponents",
      "NRM organisations",
    ],
    includes: [
      "Multiple workshops",
      "Extensive cultural and ecological mapping",
      "Restoration opportunities",
      "Threatened species",
      "Carbon opportunities",
      "Water systems",
      "Cultural landscapes",
      "Enterprise opportunities",
      "Seed collection opportunities",
      "Governance considerations",
      "Multiple map products",
    ],
    note: "This starts becoming strategic planning infrastructure.",
  },
  {
    icon: Trees,
    n: "Tier 04",
    name: "Regional Bush Mapping Program",
    price: "$80,000 – $120,000",
    tagline: "Government & philanthropic scale",
    suitable: [
      "Cape York",
      "Wet Tropics",
      "Gulf Country",
      "Torres Strait",
      "Indigenous Protected Area networks",
    ],
    includes: [
      "Multiple communities",
      "Multiple maps",
      "Regional data systems",
      "Training local rangers",
      "Knowledge transfer",
    ],
    note: "Where government and philanthropic funding sits.",
  },
];

const PROCESS = [
  {
    icon: Ear,
    title: "Listen to Country",
    body: "Initial conversations. We ask questions like:",
    questions: [
      "What makes this Country special?",
      "What species matter most?",
      "What stories should be protected?",
      "What concerns people?",
      "What opportunities do people see?",
    ],
    outputs: ["Project scope", "Permissions", "Cultural protocols"],
  },
  {
    icon: FolderSearch,
    title: "Gather Existing Knowledge",
    body: "Collect existing material that already documents Country:",
    questions: [
      "Healthy Country Plans",
      "IPA plans",
      "Species records",
      "Ecological assessments",
      "Cultural heritage reports",
      "Ranger observations",
      "Historic aerial imagery",
    ],
    aside: "This is often 50% of the work.",
  },
  {
    icon: MapPin,
    title: "On-Country Mapping",
    body: "Workshops with Elders, rangers, knowledge holders and young people.",
    capture: {
      who: ["Elders", "Rangers", "Knowledge holders", "Young people"],
      what: [
        "Species",
        "Water places",
        "Cultural sites",
        "Fire patterns",
        "Restoration priorities",
        "Seasonal indicators",
        "Bush foods",
        "Medicinal plants",
      ],
      how: ["Photos", "Audio", "GPS points", "Sketch maps", "Stories"],
    },
  },
  {
    icon: Layers,
    title: "Build the Bush Map",
    body: "Information is translated into illustrated visual layers.",
    layers: [
      { name: "Canopy", body: "Cassowary · Fruit Dove · canopy species" },
      { name: "Mid-storey", body: "Rainforest trees · bush foods" },
      { name: "Ground", body: "Frogs · reptiles · seed resources" },
      { name: "Below Ground", body: "Soils · hydrology · geology · carbon" },
    ],
  },
  {
    icon: CheckCircle,
    title: "Community Review",
    body: "Draft maps go back to community. This stage is critical.",
    questions: [
      "What's missing?",
      "What's wrong?",
      "What shouldn't be shown?",
      "What needs cultural restrictions?",
    ],
  },
  {
    icon: Package,
    title: "Final Products",
    body: "Potential outputs include:",
    questions: [
      "Wall-sized Bush Map",
      "Funding-ready PDF",
      "Ranger handbook",
      "School version",
      "Tourism version",
      "Restoration version",
      "Interactive digital version",
    ],
  },
];

const REAL_VALUE = [
  "Data collection",
  "Restoration planning",
  "Seed readiness",
  "Funding applications",
  "Ranger training",
  "Knowledge transfer",
  "Cultural governance",
];

export default function Approach() {
  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: "instant" });
  }, []);

  return (
    <main className="bg-[#FDFBF7] text-[#2D2621]">
      <Nav />

      {/* ============ Hero / Positioning ============ */}
      <section className="relative pt-32 pb-20 md:pt-40 md:pb-28 overflow-hidden">
        <div className="absolute inset-0 -z-10 opacity-20">
          <img
            src={HERO_BG}
            alt=""
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-[#FDFBF7]/85" />
        </div>

        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="bm-eyebrow mb-6">Our Approach</div>
          <h1 className="font-serif-display text-5xl sm:text-6xl lg:text-7xl leading-[1.02] text-[#2D2621] max-w-5xl">
            A Bush Map is not just a map.<br />
            <span className="italic text-[#A95A3F]">
              It&apos;s a Country intelligence product.
            </span>
          </h1>

          <div className="mt-12 grid lg:grid-cols-12 gap-10 lg:gap-16 items-start">
            <div className="lg:col-span-5">
              <p className="text-[#584D45] text-lg leading-relaxed">
                Bush Mapping sits between many disciplines — it is not GIS
                mapping. It is a synthesis of cultural knowledge, ecological
                knowledge and future opportunities, brought into a single
                visual framework that communities own.
              </p>
            </div>

            <div className="lg:col-span-7">
              <div className="text-xs tracking-[0.28em] uppercase text-[#7A6C63] mb-5">
                It sits somewhere between
              </div>
              <ul className="grid sm:grid-cols-2 gap-px bg-[#C5B6A3]/60 border border-[#C5B6A3]/60">
                {DISCIPLINES.map((d, i) => (
                  <li
                    key={d}
                    className="bg-[#FDFBF7] px-5 py-4 flex items-center gap-3"
                  >
                    <span className="font-serif-display text-sm text-[#C5B6A3] w-6">
                      {String(i + 1).padStart(2, "0")}
                    </span>
                    <span className="text-[#2D2621]">{d}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* ============ Tiers ============ */}
      <section
        id="tiers"
        data-testid="approach-tiers"
        className="py-24 md:py-32 bg-[#F3EFEA] border-t border-[#E2DACF]"
      >
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid lg:grid-cols-12 gap-10 items-end mb-16">
            <div className="lg:col-span-7">
              <div className="bm-eyebrow mb-6">Engagement Tiers</div>
              <h2 className="font-serif-display text-4xl sm:text-5xl lg:text-6xl leading-[1.05] text-[#2D2621]">
                Four ways we work together
              </h2>
            </div>
            <div className="lg:col-span-5 lg:text-right">
              <p className="text-[#584D45] text-lg leading-relaxed">
                From desk-based introductory maps to regional, multi-community
                programs. Each tier is shaped around community priorities and
                project outcomes.
              </p>
            </div>
          </div>

          <div className="grid md:grid-cols-2 gap-6 lg:gap-8">
            {TIERS.map((t) => (
              <article
                key={t.name}
                data-testid={`tier-${t.n.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                className={`relative bm-card p-8 md:p-10 flex flex-col ${
                  t.featured
                    ? "border-2 border-[#A95A3F] bg-[#FDFBF7]"
                    : ""
                }`}
              >
                {t.featured && (
                  <div className="absolute -top-3 left-8 px-3 py-1 bg-[#A95A3F] text-white text-[10px] tracking-[0.24em] uppercase font-semibold flex items-center gap-1.5">
                    <Star size={11} /> Sweet spot
                  </div>
                )}
                <div className="flex items-start justify-between mb-6">
                  <span className="w-14 h-14 rounded-full bg-[#2C3C2B] text-[#E9C7A8] flex items-center justify-center">
                    <t.icon size={22} />
                  </span>
                  <div className="text-right">
                    <div className="text-[10px] tracking-[0.28em] uppercase text-[#A95A3F] font-semibold">
                      {t.n}
                    </div>
                    <div className="font-serif-display text-2xl text-[#2D2621] mt-1">
                      {t.price}
                    </div>
                  </div>
                </div>

                <h3 className="font-serif-display text-2xl md:text-3xl text-[#2D2621] leading-tight">
                  {t.name}
                </h3>
                <p className="mt-2 text-[#A95A3F] text-sm tracking-wide italic">
                  {t.tagline}
                </p>

                <div className="mt-7">
                  <div className="text-[10px] tracking-[0.24em] uppercase text-[#7A6C63] mb-3">
                    Suitable for
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {t.suitable.map((s) => (
                      <span
                        key={s}
                        className="px-3 py-1 text-[12px] bg-[#EBE4D8] text-[#2D2621] border border-[#C5B6A3]"
                      >
                        {s}
                      </span>
                    ))}
                  </div>
                </div>

                <div className="mt-6">
                  <div className="text-[10px] tracking-[0.24em] uppercase text-[#7A6C63] mb-3">
                    Includes
                  </div>
                  <ul className="space-y-2.5">
                    {t.includes.map((it) => (
                      <li
                        key={it}
                        className="flex items-start gap-3 text-[15px] text-[#2D2621] leading-snug"
                      >
                        <Check
                          size={14}
                          className="mt-1 text-[#A95A3F] shrink-0"
                        />
                        {it}
                      </li>
                    ))}
                  </ul>
                </div>

                <div className="mt-auto pt-7">
                  <div className="bm-hairline mb-4" />
                  <p className="text-[#584D45] text-sm italic leading-relaxed">
                    {t.note}
                  </p>
                </div>
              </article>
            ))}
          </div>

          <p className="mt-10 text-[#7A6C63] text-sm max-w-2xl">
            Indicative ranges. Every project is scoped against community
            priorities, cultural protocols and the work required.
          </p>
        </div>
      </section>

      {/* ============ Process (rich version) ============ */}
      <section
        id="approach-process"
        data-testid="approach-process"
        className="py-24 md:py-32 bg-[#FDFBF7]"
      >
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="max-w-3xl mb-16">
            <div className="bm-eyebrow mb-6">Consulting Process</div>
            <h2 className="font-serif-display text-4xl sm:text-5xl lg:text-6xl leading-[1.05] text-[#2D2621]">
              Six stages of working with Country
            </h2>
            <p className="mt-6 text-[#584D45] text-lg leading-relaxed">
              Bush Mapping is a structured process — but every stage is
              shaped, paced and approved by community.
            </p>
          </div>

          <ol className="relative">
            <span
              className="absolute left-[28px] md:left-[35px] top-3 bottom-3 w-px bg-[#C5B6A3]"
              aria-hidden="true"
            />
            {PROCESS.map((s, i) => (
              <li
                key={s.title}
                className="relative pl-20 md:pl-28 pb-12 md:pb-14 last:pb-0"
              >
                <div className="absolute left-0 top-0">
                  <span className="w-14 h-14 md:w-[70px] md:h-[70px] rounded-full bg-[#F3EFEA] border border-[#C5B6A3] flex items-center justify-center text-[#A95A3F] shadow-sm">
                    <s.icon size={22} />
                  </span>
                </div>

                <div className="bm-card bg-[#F3EFEA] p-7 md:p-9">
                  <div className="flex items-baseline gap-4 mb-4">
                    <span className="text-[10px] tracking-[0.28em] uppercase text-[#A95A3F] font-semibold">
                      Stage {i + 1}
                    </span>
                    <span className="h-px flex-1 bg-[#C5B6A3]/60" />
                  </div>
                  <h3 className="font-serif-display text-2xl md:text-3xl text-[#2D2621] leading-tight">
                    {s.title}
                  </h3>
                  <p className="mt-4 text-[#584D45] leading-relaxed">
                    {s.body}
                  </p>

                  {s.questions && (
                    <ul className="mt-5 grid sm:grid-cols-2 gap-x-8 gap-y-2">
                      {s.questions.map((q) => (
                        <li
                          key={q}
                          className="flex items-start gap-3 text-[#2D2621] text-[15px] leading-relaxed"
                        >
                          <span className="mt-2.5 w-1.5 h-1.5 rounded-full bg-[#6B7A6A] shrink-0" />
                          {q}
                        </li>
                      ))}
                    </ul>
                  )}

                  {s.outputs && (
                    <div className="mt-6">
                      <div className="text-[10px] tracking-[0.24em] uppercase text-[#7A6C63] mb-2">
                        Outputs
                      </div>
                      <div className="flex flex-wrap gap-2">
                        {s.outputs.map((o) => (
                          <span
                            key={o}
                            className="px-3 py-1 bg-[#FDFBF7] border border-[#C5B6A3] text-[13px]"
                          >
                            {o}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}

                  {s.capture && (
                    <div className="mt-6 grid md:grid-cols-3 gap-4">
                      <CaptureBlock title="Who" items={s.capture.who} />
                      <CaptureBlock title="What we capture" items={s.capture.what} />
                      <CaptureBlock title="How" items={s.capture.how} />
                    </div>
                  )}

                  {s.layers && (
                    <div className="mt-6 grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
                      {s.layers.map((l) => (
                        <div
                          key={l.name}
                          className="p-4 border border-[#C5B6A3]/60 bg-[#FDFBF7]"
                        >
                          <div className="text-[10px] tracking-[0.24em] uppercase font-semibold text-[#A95A3F]">
                            {l.name}
                          </div>
                          <div className="mt-2 text-[#2D2621] text-[14px] leading-snug">
                            {l.body}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}

                  {s.aside && (
                    <div className="mt-6 inline-block px-4 py-2 border-l-2 border-[#A95A3F] bg-[#FDFBF7] font-serif-display italic text-lg text-[#2C3C2B]">
                      {s.aside}
                    </div>
                  )}
                </div>
              </li>
            ))}
          </ol>
        </div>
      </section>

      {/* ============ Big Opportunity ============ */}
      <section
        id="opportunity"
        data-testid="approach-opportunity"
        className="py-24 md:py-32 bg-[#2C3C2B] text-[#FDFBF7] bm-noise relative"
      >
        <div className="relative z-10 max-w-6xl mx-auto px-6 lg:px-8">
          <div className="max-w-3xl">
            <div className="text-[11px] tracking-[0.28em] uppercase text-[#E9C7A8] font-semibold mb-6">
              Where the real value sits
            </div>
            <h2 className="font-serif-display text-4xl sm:text-5xl lg:text-6xl leading-[1.05]">
              We&apos;re not just making maps.
            </h2>
            <p className="mt-8 text-[#D7CFC0] text-lg leading-relaxed">
              We&apos;re helping people understand, communicate and plan for
              Country through a visual framework that brings cultural
              knowledge, ecological knowledge and future opportunities together.
            </p>
          </div>

          <ul className="mt-14 grid sm:grid-cols-2 lg:grid-cols-3 gap-px bg-[#3A4D39]">
            {REAL_VALUE.map((v) => (
              <li
                key={v}
                className="bg-[#2C3C2B] px-6 py-6 flex items-center gap-3 hover:bg-[#34452F] transition-colors"
              >
                <Sparkles size={16} className="text-[#E9C7A8] shrink-0" />
                <span className="font-serif-display text-xl">{v}</span>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* ============ Free Download ============ */}
      <DownloadPromo />

      {/* ============ CTA ============ */}
      <section className="py-20 md:py-28 bg-[#EBE4D8]">
        <div className="max-w-5xl mx-auto px-6 lg:px-8 text-center">
          <div className="bm-eyebrow mb-6">Next step</div>
          <h2 className="font-serif-display text-4xl sm:text-5xl lg:text-6xl leading-[1.05] text-[#2D2621]">
            Let&apos;s talk about your Country.
          </h2>
          <p className="mt-6 text-[#584D45] text-lg leading-relaxed max-w-2xl mx-auto">
            We start every project with a Discovery Call — to listen, to
            understand cultural protocols and to scope what a Bush Map could
            look like for your community.
          </p>
          <div className="mt-10 flex flex-wrap gap-4 justify-center">
            <Link
              to="/#book"
              data-testid="approach-cta-contact"
              className="bm-btn-primary"
            >
              Book a Discovery Call <ArrowRight size={16} />
            </Link>
            <Link
              to="/"
              data-testid="approach-cta-home"
              className="bm-btn-outline"
            >
              Back to overview
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </main>
  );
}

function CaptureBlock({ title, items }) {
  return (
    <div className="p-4 border border-[#C5B6A3]/60 bg-[#FDFBF7]">
      <div className="text-[10px] tracking-[0.24em] uppercase font-semibold text-[#A95A3F] mb-3">
        {title}
      </div>
      <ul className="space-y-1.5">
        {items.map((it) => (
          <li key={it} className="text-[#2D2621] text-[14px] leading-snug">
            {it}
          </li>
        ))}
      </ul>
    </div>
  );
}
